import { isDefined, isEmpty, isNotDefined } from '../isEmpty'
import {
  type AccountData,
  type TagId,
  type TinyWorkflowAccount,
  type UserId,
  WORKFLOW_PROSPECT_STAGE_ID,
  type WorkflowActionId,
  type WorkflowHistoryItem,
  type WorkflowStageId,
} from '../../model'
import { findLatestWorkflowStage } from './stage'
import { findLatestWorkflowAction } from './lastAction'

export function decorateAccountWithLatestStatus<T extends AccountData>(account: T): T {
  if (isEmpty(account.workflowId)) return account

  const latest = findLatestWorkflowStage(account.workflowHistory)
  const latestAction = findLatestWorkflowAction(account.workflowHistory)

  return {
    ...account,
    lastActionDate: latest?.timestamp || account.lastActionDate,
    workflowStageId: latest?.stageId || account.workflowStageId,
    workflowActionId: latestAction?.actionId,
  } satisfies T
}

export function toTinyWorkflowAccount(
  account?: Pick<
    AccountData,
    | 'accountId'
    | 'workflowId'
    | 'workflowHistory'
    | 'workflowActionId'
    | 'workflowStageId'
    | 'lastActionDate'
    | 'customFields'
  >
): TinyWorkflowAccount | undefined {
  if (isEmpty(account)) return
  if (isEmpty(account.workflowId)) return
  if (isEmpty(account.workflowStageId)) return

  const latest = findLatestWorkflowStage(account.workflowHistory)
  const latestAction = findLatestWorkflowAction(account.workflowHistory)

  return {
    accountId: account.accountId,
    workflowId: account.workflowId,
    workflowHistory: account.workflowHistory || [],
    workflowStageId: latest?.stageId ?? account.workflowStageId,
    workflowLastActionDate: latest?.timestamp ?? account.lastActionDate,
    workflowActionId: latestAction?.actionId,
    customFields: account.customFields,
  }
}

export const getWorkflowAccountTimeline = ({
  workflowHistory,
}: Pick<AccountData, 'workflowHistory'>): WorkflowHistoryItem[] => {
  if (isEmpty(workflowHistory)) return []
  return [...workflowHistory].sort((a, b) => {
    if (a.timestamp === b.timestamp) return workflowHistory.indexOf(b) - workflowHistory.indexOf(a)
    return a.timestamp - b.timestamp
  })
}

export type WorkflowAccountLastActivity = {
  stageId: WorkflowStageId
  actionId?: WorkflowActionId | TagId
  userId: UserId
  timestamp: number
}
export const getWorkflowAccountLastActivity = (
  account: Pick<AccountData, 'workflowHistory' | 'tags' | 'ownerId'>,
  shouldIncludeTags?: boolean
): WorkflowAccountLastActivity | undefined => {
  const timeline = getWorkflowAccountTimeline(account)
  const lastItem = timeline?.at(-1)
  const lastTag = account?.tags?.sort((a, b) => a.timestamp - b.timestamp).at(-1)

  const stageId = lastItem?.stageId ?? WORKFLOW_PROSPECT_STAGE_ID

  if (isDefined(lastItem) && (!shouldIncludeTags || isNotDefined(lastTag) || lastItem.timestamp > lastTag.timestamp)) {
    return {
      stageId,
      actionId: lastItem.actionId,
      userId: lastItem.userId,
      timestamp: lastItem.timestamp,
    }
  }
  if (isDefined(lastTag)) {
    return {
      stageId,
      actionId: lastTag.tagId,
      timestamp: lastTag.timestamp,
      userId: lastTag.userId,
    }
  }
}
